import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { ukDayEnd, ukDayStart } from "@/constants/sheriffOptions"
import { supabase } from "@/lib/supabase"
import {
  type ClockInBody,
  type ClockOutBody,
  type CreateUnavailabilityBody,
  type Timesheet as SvcTimesheet,
  type Unavailability as SvcUnavailability,
  type TimesheetStatus,
  type UpdateTimesheetBody,
  timesheetsService,
} from "@/services/timesheetsService"
import { Timesheet, TimesheetFilters, TimesheetStats } from "@/types/timesheet"

type RangeFilterable<Q> = {
  gte: (column: string, value: string) => Q
  lte: (column: string, value: string) => Q
}

/** Limits a query to the clock-ins within the UK days `filters` asks for. */
const withClockedInRange = <Q extends RangeFilterable<Q>>(
  query: Q,
  filters: Pick<TimesheetFilters, "date_from" | "date_to">,
): Q => {
  let ranged = query
  if (filters.date_from) {
    ranged = ranged.gte("clocked_in_time", ukDayStart(filters.date_from).toISOString())
  }
  if (filters.date_to) {
    ranged = ranged.lte("clocked_in_time", ukDayEnd(filters.date_to).toISOString())
  }
  return ranged
}

// Hook for unreviewed (Pending) timesheets - loads all without pagination
export const useUnreviewedTimesheets = (filters: Omit<TimesheetFilters, "status"> = {}) => {
  return useQuery({
    queryKey: ["timesheets", "unreviewed", filters],
    queryFn: async () => {
      let query = supabase
        .from("timesheets")
        .select(
          `
          *,
          person:people!person_id(id, first_name, surname, email, person),
          shift:shifts!timesheets_shift_id_fkey(id, start_time, end_time, area, type, location, employer, notes, depot_id)
        `,
        )
        .eq("status", "Pending")
        .order("clocked_in_time", { ascending: false })

      query = withClockedInRange(query, filters)

      const { data, error } = await query
      if (error) throw error
      return data as Timesheet[]
    },
  })
}

// Hook for reviewed timesheets with pagination (all non-pending: Approved, Denied, InProgress)
export const useReviewedTimesheets = (
  filters: Omit<TimesheetFilters, "status"> & {
    page?: number
    pageSize?: number
  } = {},
) => {
  const page = filters.page || 1
  const pageSize = filters.pageSize || 50
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  return useQuery({
    queryKey: ["timesheets", "reviewed", filters],
    queryFn: async () => {
      let query = supabase
        .from("timesheets")
        .select(
          `
          *,
          person:people!person_id(id, first_name, surname, email, person),
          shift:shifts!timesheets_shift_id_fkey(id, start_time, end_time, area, type, location, employer, notes, depot_id)
        `,
          { count: "exact" },
        )
        .in("status", ["Approved", "Denied", "InProgress"])
        .order("updated_at", { ascending: false })
        .range(from, to)

      query = withClockedInRange(query, filters)

      const { data, error, count } = await query
      if (error) throw error
      return { data: data as Timesheet[], count: count || 0 }
    },
  })
}

// Legacy hook for backward compatibility - now defaults to Pending only
export const useTimesheets = (filters: TimesheetFilters = {}) => {
  return useQuery({
    queryKey: ["timesheets", filters],
    queryFn: async () => {
      let query = supabase
        .from("timesheets")
        .select(
          `
          *,
          person:people!person_id(id, first_name, surname, email, person),
          shift:shifts!timesheets_shift_id_fkey(id, start_time, end_time, area, type, location, employer, notes, depot_id)
        `,
        )
        .order("clocked_in_time", { ascending: false })

      if (filters.status) {
        query = query.eq("status", filters.status)
      } else {
        // Default to Pending only to avoid loading all timesheets
        query = query.eq("status", "Pending")
      }

      query = withClockedInRange(query, filters)

      const { data, error } = await query
      if (error) throw error
      return data as Timesheet[]
    },
  })
}

export const useTimesheet = (id: string) => {
  return useQuery({
    queryKey: ["timesheet", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheets")
        .select(
          `
          *,
          person:people!person_id(id, first_name, surname, email, person),
          shift:shifts!timesheets_shift_id_fkey(id, start_time, end_time, area, type, location, employer, notes, depot_id)
        `,
        )
        .eq("id", id)
        .single()

      if (error) throw error
      return data as Timesheet
    },
    enabled: !!id,
  })
}

export const useTimesheetStats = (filters: TimesheetFilters = {}) => {
  return useQuery({
    queryKey: ["timesheet-stats", filters],
    queryFn: async () => {
      // Use aggregate queries for better performance
      const calculateHours = (t: {
        clocked_in_time?: string
        clocked_out_time?: string
        break?: number
      }) => {
        if (!t.clocked_in_time || !t.clocked_out_time) return 0
        const diff = new Date(t.clocked_out_time).getTime() - new Date(t.clocked_in_time).getTime()
        const hours = diff / (1000 * 60 * 60)
        const breakHours = (t.break || 0) / 60
        return Math.max(0, hours - breakHours)
      }

      // Build base query with date filters
      const buildQuery = () =>
        withClockedInRange(
          supabase.from("timesheets").select("status, clocked_in_time, clocked_out_time, break"),
          filters,
        )

      // Fetch counts by status (lightweight)
      const [pendingResult, approvedResult, deniedResult, inProgressResult] = await Promise.all([
        buildQuery().eq("status", "Pending"),
        buildQuery().eq("status", "Approved"),
        buildQuery().eq("status", "Denied"),
        buildQuery().eq("status", "InProgress"),
      ])

      if (pendingResult.error) throw pendingResult.error
      if (approvedResult.error) throw approvedResult.error
      if (deniedResult.error) throw deniedResult.error
      if (inProgressResult.error) throw inProgressResult.error

      const pendingData = pendingResult.data || []
      const approvedData = approvedResult.data || []
      const deniedData = deniedResult.data || []
      const inProgressData = inProgressResult.data || []
      const allData = [...pendingData, ...approvedData, ...deniedData, ...inProgressData]

      const stats: TimesheetStats = {
        total_timesheets: allData.length,
        pending_approval: pendingData.length,
        approved: approvedData.length,
        rejected: deniedData.length,
        total_hours: allData.reduce((sum, t) => sum + calculateHours(t), 0),
        average_hours_per_timesheet:
          allData.length > 0
            ? allData.reduce((sum, t) => sum + calculateHours(t), 0) / allData.length
            : 0,
      }

      return stats
    },
  })
}

export const useClockIn = () => {
  return useMutation<SvcTimesheet, Error, ClockInBody>({
    mutationFn: (body) => timesheetsService.clockIn(body),
  })
}

export const useClockOut = () => {
  return useMutation<SvcTimesheet, Error, ClockOutBody>({
    mutationFn: (body) => timesheetsService.clockOut(body),
  })
}

export const useUpdateTimesheet = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: {
        clocked_in_time?: string
        clocked_out_time?: string
        break?: number
        status?: string
      }
    }) => {
      await timesheetsService.updateTimesheet({
        id,
        clockedInTime: data.clocked_in_time,
        clockedOutTime: data.clocked_out_time,
        break: data.break,
        status: data.status as TimesheetStatus | undefined,
      })
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["timesheet", id] })
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
      queryClient.invalidateQueries({ queryKey: ["timesheet-stats"] })
    },
  })
}

// Note: The actual timesheets table uses a simple 'break' integer field
// Break and entry management hooks removed as they don't match the current schema

export const useApproveTimesheet = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await timesheetsService.updateTimesheet({
        id,
        status: "Approved",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
      queryClient.invalidateQueries({ queryKey: ["timesheet-stats"] })
    },
  })
}

export const useRejectTimesheet = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id }: { id: string; reason: string }) => {
      // Note: timesheets table doesn't have rejection_reason field
      // Reason is logged but not persisted in the database
      await timesheetsService.updateTimesheet({
        id,
        status: "Denied",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
      queryClient.invalidateQueries({ queryKey: ["timesheet-stats"] })
    },
  })
}

export const useUpdateTimesheetService = () => {
  return useMutation<SvcTimesheet, Error, UpdateTimesheetBody>({
    mutationFn: (body) => timesheetsService.updateTimesheet(body),
  })
}

export const useCreateUnavailability = () => {
  return useMutation<SvcUnavailability, Error, CreateUnavailabilityBody>({
    mutationFn: (body) => timesheetsService.createUnavailability(body),
  })
}
